import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { prefetchNavPath } from "@/app/routeModules";
import { cn } from "@/lib/utils";
import { useFeed } from "@/context/FeedContext";
import { useRightRail } from "@/context/RightRailContext";
import { FeedEventRow } from "@/features/feed/FeedEventRow";
import { FeedCutoffGroupRow } from "@/features/feed/FeedCutoffGroupRow";
import { FeedListLoading } from "@/features/feed/FeedListLoading";
import { collapseCutoffEvents } from "@/features/feed/feedCutoffGroups";
import { FeedRailFilterControls } from "@/features/feed/FeedRailFilterControls";

interface NotificationsListProps {
  entering?: boolean;
  exiting?: boolean;
  className?: string;
}

export function NotificationsList({
  entering = false,
  exiting = false,
  className,
}: NotificationsListProps) {
  const { events, loading, error, unreadCount, markAllSeen, filters } = useFeed();
  const { setMobileRailOpen } = useRightRail();
  // Collapse every cutoff-approaching event into one summary card at the newest
  // cutoff event's slot; all other events keep rendering individually.
  const displayItems = useMemo(() => collapseCutoffEvents(events), [events]);
  const rowCount = displayItems.length;

  const emptyMessage =
    filters.reviewStatus === "reviewed"
      ? "No reviewed events yet. Mark events as reviewed to file them here."
      : filters.scopeDimension === "sales_person" && !filters.scopeKey
        ? "No aggregate sales-person trends yet. Pick a rep to see their bookings."
        : filters.scopeDimension === "customer" && !filters.scopeKey
          ? "No aggregate customer trends yet. Pick a customer to see their bookings."
          : filters.scopeDimension === "operator" && !filters.scopeKey
            ? "No operator-correlated insights or alerts yet. Pick an operator to see their feed."
            : "No activity yet. Events appear here as data is ingested and processed.";

  // Opening the panel counts as seeing the current feed — clear the badge.
  useEffect(() => {
    if (entering && unreadCount > 0) {
      markAllSeen();
    }
    // Intentionally depends on `entering` only: mark-seen fires on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entering]);

  return (
    <div
      className={cn(
        "feed-event-list flex w-full min-w-0 max-w-full flex-col gap-2",
        className,
      )}
      role="list"
      aria-label="Activity feed"
    >
      <header className="feed-rail-header px-1">
        <FeedRailFilterControls />
        <Link
          to="/feed"
          onMouseEnter={() => prefetchNavPath("/feed")}
          onFocus={() => prefetchNavPath("/feed")}
          onClick={() => setMobileRailOpen(false)}
          className="feed-rail-view-all feed-filter-chip feed-filter-chip--active inline-flex items-center gap-1 no-underline"
        >
          View all
          <ArrowUpRight className="size-3" />
        </Link>
      </header>

      {loading && events.length === 0 && <FeedListLoading compact />}

      {error && events.length === 0 && (
        <p className="px-3 py-4 text-xs text-rose-500">{error}</p>
      )}

      {!loading && !error && events.length === 0 && (
        <p className="px-3 py-4 text-xs text-muted-foreground">{emptyMessage}</p>
      )}

      {displayItems.length > 0 && (
        <div className="feed-row-stack">
          {displayItems.map((item, index) => {
            if (item.kind === "cutoff_group") {
              return (
                <FeedCutoffGroupRow key="cutoff-group" events={item.events} />
              );
            }
            return (
              <FeedEventRow
                key={item.event.id}
                event={item.event}
                index={index}
                entering={entering && !exiting}
                exiting={exiting}
                exitDelayMs={(rowCount - 1 - index) * 50}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
